import React from "react";

/**
 * CityDooh brand mark — official 09/08 v3 refresh.
 *
 * ONLY the three approved master assets are ever rendered:
 *   /citydooh-logo-horizontal.png   1757×618, black CITY + gold DOOH, transparent PNG
 *                                    → drop on cream / white / light surfaces
 *   /citydooh-logo-inverse.png      1757×618, cream/white CITY + gold DOOH, transparent PNG
 *                                    → drop DIRECTLY on black surfaces (no chip / no filter)
 *   /citydooh-icon.png              557×602, standalone symbol only
 *                                    → collapsed sidebars, small watermarks, PWA/favicon
 *
 * Sizing is strictly WIDTH-based; height is `auto` so the natural
 * 2.84:1 ratio is preserved on every viewport. There is no
 * `max-height` guard — clamping height compresses the artwork and is
 * explicitly forbidden by the brand rules.
 */

const HORIZONTAL_ASSET = {
  light: "/citydooh-logo-horizontal.png",
  dark:  "/citydooh-logo-inverse.png",
};
const ICON_ASSET = "/citydooh-icon.png";

// Approved responsive widths (spec §5, §7, §8, §10, §11, §12).
// Each "size" preset maps to a placement recipe.
const HORIZONTAL_WIDTH_CLASSES = {
  // Public site header, Explore, Offer, "For businesses" nav.
  header: "w-[108px] sm:w-[128px] md:w-[145px] lg:w-[170px]",
  // Site footer / For-Businesses dark strip.
  footer: "w-[135px] sm:w-[150px] md:w-[165px] lg:w-[178px]",
  // Auth pages (customer + vendor login/register/OTP).
  auth: "w-[120px] sm:w-[135px] md:w-[150px] lg:w-[160px]",
  // Vendor + admin expanded dark sidebar.
  sidebar: "w-[128px]",
  // Mobile vendor/admin top bar.
  mobileBar: "w-[104px]",
  // Game/offer top-left "Powered by" watermark.
  game: "w-[90px] sm:w-[110px] md:w-[130px]",
  // Legacy passthroughs so older call sites still render sane widths.
  sm: "w-[120px] sm:w-[128px]",
  md: "w-[128px] sm:w-[145px] md:w-[165px] lg:w-[170px]",
  lg: "w-[145px] sm:w-[160px] md:w-[172px] lg:w-[180px]",
};

// Icon-only preset widths for the standalone symbol.
const ICON_WIDTHS = {
  // Collapsed vendor/admin sidebar (spec §10, §11: 36-40px).
  sidebar: "w-[38px]",
  // Small compact avatar-slot / loading spinner watermark.
  sm: "w-[28px]",
  md: "w-[40px]",
  lg: "w-[56px]",
};

/**
 * <BrandMark />
 *
 * Props:
 *   • onDark   — render the inverse (cream+gold) lockup on dark surfaces.
 *   • variant  — "horizontal" (default) | "icon"
 *   • size     — semantic preset (see HORIZONTAL_WIDTH_CLASSES / ICON_WIDTHS)
 *                back-compat: "sm" | "md" | "lg" still supported.
 *   • className— extra classes on the wrapping <span>.
 *
 * All logos link-wrap externally; this component intentionally renders
 * NO <a>, NO chip, NO shadow — the caller controls the anchor + focus
 * ring per accessibility rules (spec §17).
 */
export function BrandMark({
  onDark = false,
  variant = "horizontal",
  size = "header",
  className = "",
  alt = "City DOOH",
  ...rest
}) {
  if (variant === "icon") {
    const widthCls = ICON_WIDTHS[size] || ICON_WIDTHS.md;
    return (
      <span className={`inline-flex shrink-0 items-center justify-center ${className}`} {...rest}>
        <img
          src={ICON_ASSET}
          alt={alt}
          width="557"
          height="602"
          draggable="false"
          className={`block ${widthCls} h-auto object-contain select-none`}
          data-testid="brandmark-icon"
        />
      </span>
    );
  }

  const widthCls =
    HORIZONTAL_WIDTH_CLASSES[size] || HORIZONTAL_WIDTH_CLASSES.header;
  const src = onDark ? HORIZONTAL_ASSET.dark : HORIZONTAL_ASSET.light;

  return (
    <span className={`inline-flex shrink-0 items-center ${className}`} {...rest}>
      <img
        src={src}
        alt={alt}
        width="1757"
        height="618"
        draggable="false"
        className={`block ${widthCls} h-auto object-contain object-left select-none`}
        data-testid={`brandmark-horizontal${onDark ? "-onDark" : ""}`}
      />
    </span>
  );
}

export default BrandMark;
